import React from 'react';

/**
 * ONE liquid body that flows from the active tab to the next one.
 *
 * ---------------------------------------------------------------------------
 * Why the previous version looked like "two pills"
 * ---------------------------------------------------------------------------
 * It was a single <span> whose width was animated. Mid-flight that span was
 * 485px wide and still 32px TALL, so it lay across four tabs at once as a
 * solid bar, with their dark labels sitting on top of it. Nothing was
 * duplicated — a full-height rectangle spanning the whole journey simply does
 * not read as one travelling pill.
 *
 * A liquid does not do that. It necks: the middle thins to a thread while the
 * two ends stay round. That is the fix, and it is geometric rather than a
 * matter of timing.
 *
 * ---------------------------------------------------------------------------
 * The model
 * ---------------------------------------------------------------------------
 * One <path>, redrawn each frame. Never two shapes cross-fading, never a clone
 * chasing the original — those are what produce ghosting.
 *
 * The outline is generated from a half-height function h(x):
 *
 *   • round caps      h(x) follows a circle at each end, so the ends are pills
 *   • the neck        h(x) is multiplied by a gaussian dip at the centre
 *   • the travel      the LEADING edge leaves first, the TRAILING edge follows,
 *                     so the body stretches, necks, and is drawn after it
 *
 * Both edges are driven by smootherstep (6t⁵−15t⁴+10t³), which has zero
 * velocity at both ends — it cannot snap at the start or stop abruptly, and it
 * never overshoots, so there is no bounce.
 */

// Zero velocity at t=0 and t=1. The calm, no-flick curve.
const smootherstep = (t) => {
  const k = Math.min(1, Math.max(0, t));
  return k * k * k * (k * (k * 6 - 15) + 10);
};

/** Half the body's height at x.
 *
 *  A TRUE PILL: circular caps, flat middle. There is deliberately no neck.
 *
 *  The previous version multiplied this by a gaussian dip to make a neck, and
 *  that is what produced the ribbon. Every tab label's text centre sits on the
 *  same baseline as the indicator's centre, so a neck does not pass BELOW the
 *  text — it passes straight THROUGH it, striking out every label it crosses.
 *  No amount of thinning fixes that; the shape has to stop spanning tabs.
 */
function halfHeight(x, L, R, H) {
  const r = H / 2;
  if (x < L + r) {
    const dx = L + r - x;
    return Math.sqrt(Math.max(0, r * r - dx * dx));
  }
  if (x > R - r) {
    const dx = x - (R - r);
    return Math.sqrt(Math.max(0, r * r - dx * dx));
  }
  return r;
}

/** Catmull-Rom through the sampled points, emitted as cubic Béziers, so the
 *  surface curves continuously instead of showing the sampling as facets. */
function smoothClosedPath(pts) {
  if (pts.length < 2) return '';
  const n = pts.length;
  let d = `M ${pts[0][0].toFixed(2)} ${pts[0][1].toFixed(2)}`;
  for (let i = 0; i < n; i += 1) {
    const p0 = pts[(i - 1 + n) % n];
    const p1 = pts[i];
    const p2 = pts[(i + 1) % n];
    const p3 = pts[(i + 2) % n];
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${c1x.toFixed(2)} ${c1y.toFixed(2)}, ${c2x.toFixed(2)} ${c2y.toFixed(2)},`
       + ` ${p2[0].toFixed(2)} ${p2[1].toFixed(2)}`;
  }
  return `${d} Z`;
}

/** Outline of the body spanning [L,R], height H, centred on cy.
 *
 *  Sampled and smoothed rather than emitted as a rounded <rect> so the edges
 *  can deform gently while travelling — that softness is the whole liquid
 *  reading, and it stays inside the pill's own footprint. */
function bodyPath(L, R, H, cy, SAMPLES = 40) {
  const span = Math.max(1, R - L);
  const top = [];
  const bottom = [];
  for (let i = 0; i <= SAMPLES; i += 1) {
    const x = L + (span * i) / SAMPLES;
    const h = halfHeight(x, L, R, H);
    top.push([x, cy - h]);
    bottom.push([x, cy + h]);
  }
  return smoothClosedPath([...top, ...bottom.reverse()]);
}

export default function LiquidIndicator({
  activeSelector,
  gradientId = 'liquid-grad',
  // ~1050ms: calm and deliberate, while keeping any label the body
  // passes under covered only briefly.
  duration = 1050,
  className = '',
}) {
  const svg = React.useRef(null);
  const path = React.useRef(null);
  const [ready, setReady] = React.useState(false);

  React.useLayoutEffect(() => {
    // The svg is rendered inside the container, so its parent IS the container.
    // A parent's ref would still be null here: React attaches child refs before
    // parent refs, so the first measurement would be skipped entirely.
    const box = svg.current?.parentElement;
    if (!box) return undefined;

    const still = window.matchMedia('(prefers-reduced-motion: reduce)');
    let at = null;        // the rect the body is resting on
    let raf = 0;          // the ONE animation in flight; retargeting cancels it
    let first = true;

    const rectOf = (el) => {
      const a = el.getBoundingClientRect();
      const c = box.getBoundingClientRect();
      return { x: a.left - c.left + box.scrollLeft, y: a.top - c.top + box.scrollTop,
               w: a.width, h: a.height };
    };

    // Labels the body is currently sitting under. They are flipped to white so
    // they stay legible while it passes — the text itself never moves, fades or
    // duplicates, only its colour changes.
    const markCovered = (L, R) => {
      box.querySelectorAll('[role="tab"]').forEach((t) => {
        const r = t.getBoundingClientRect();
        const c = box.getBoundingClientRect();
        const tl = r.left - c.left + box.scrollLeft;
        const tr = tl + r.width;
        // Generous overlap before flipping, so a label only turns white once
        // the body is genuinely behind it rather than merely adjacent.
        const covered = Math.min(tr, R) - Math.max(tl, L) > r.width * 0.45;
        t.toggleAttribute('data-liquid-over', covered);
      });
    };

    const draw = (L, R, H, cy) => {
      if (path.current) path.current.setAttribute('d', bodyPath(L, R, H, cy));
      markCovered(L, R);
    };

    const rest = (r) => {
      // Sizing the canvas to the container keeps the path in container
      // coordinates, so no scaling is ever applied to the geometry.
      const c = box.getBoundingClientRect();
      if (svg.current) {
        svg.current.setAttribute('width', `${c.width}`);
        svg.current.setAttribute('height', `${c.height}`);
        svg.current.setAttribute('viewBox', `0 0 ${c.width} ${c.height}`);
      }
      draw(r.x, r.x + r.w, r.h, r.y + r.h / 2);
      at = r;
      setReady(true);
      box.setAttribute('data-indicator-ready', '');
    };

    const place = () => {
      const active = box.querySelector(activeSelector);
      if (!active) {
        setReady(false);
        box.removeAttribute('data-indicator-ready');   // CSS fallback paints
        return;
      }
      const to = rectOf(active);
      const same = at && Math.abs(at.x - to.x) < 0.5 && Math.abs(at.w - to.w) < 0.5
        && Math.abs(at.y - to.y) < 0.5;

      if (first || !at || same || still.matches) {
        first = false;
        cancelAnimationFrame(raf);           // never leave a stale flight
        rest(to);
        return;
      }

      // Retarget cleanly: one body, one flight. A rapid second click cancels
      // the first rather than stacking a second animation on the same path,
      // which is what would leave trails.
      cancelAnimationFrame(raf);
      const from = at;
      at = to;                                // destination is authoritative now
      const rightward = to.x >= from.x;
      const t0 = performance.now();

      const step = (now) => {
        const p = Math.min(1, (now - t0) / duration);
        const e = smootherstep(p);

        // The body travels as ONE PILL. Its centre moves; its width breathes.
        // It is never allowed to span the gap, which is what kept the old
        // shape lying across three labels at text height.
        const cFrom = from.x + from.w / 2;
        const cTo = to.x + to.w / 2;
        const cx = cFrom + (cTo - cFrom) * e;
        const baseW = from.w + (to.w - from.w) * e;

        // Bounded stretch: at most ~28% of its own width, however far it
        // travels. This is the cap that makes a ribbon impossible — the body
        // can never be wider than roughly one tab plus a little.
        const dist = Math.abs(cTo - cFrom);
        const boost = Math.min(0.28 * Math.max(from.w, to.w), dist * 0.18)
          * Math.sin(Math.PI * p) ** 0.9;
        const w = baseW + boost;

        // A touch of vertical squash as it elongates — volume reads as
        // conserved, which is what makes a stretching shape feel like liquid
        // rather than a rectangle being scaled.
        const H = (from.h + (to.h - from.h) * e) * (1 - 0.055 * Math.sin(Math.PI * p));
        const cy = (from.y + from.h / 2) + ((to.y + to.h / 2) - (from.y + from.h / 2)) * e;

        // Gentle lean: the leading edge runs a little ahead of the centre, the
        // trailing edge lags by the same amount. Bounded by `boost`, so it
        // softens the edges without extending the footprint.
        const lean = (rightward ? 1 : -1) * boost * 0.18;
        draw(cx - w / 2 + lean, cx + w / 2 + lean, H, cy);

        if (p < 1) raf = requestAnimationFrame(step);
        else rest(to);
      };
      raf = requestAnimationFrame(step);
    };

    place();
    if (document.fonts?.ready) document.fonts.ready.then(place).catch(() => {});

    const mo = new MutationObserver(place);
    mo.observe(box, { attributes: true, childList: true, subtree: true,
                      attributeFilter: ['data-state', 'aria-current', 'class'] });
    const ro = new ResizeObserver(() => { if (at) { cancelAnimationFrame(raf); place(); } });
    ro.observe(box);
    Array.from(box.children).forEach((c) => ro.observe(c));
    window.addEventListener('resize', place);
    return () => {
      cancelAnimationFrame(raf);
      mo.disconnect(); ro.disconnect();
      window.removeEventListener('resize', place);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSelector, duration]);

  return (
    <svg
      ref={svg}
      aria-hidden="true"
      focusable="false"
      data-liquid-indicator=""
      className={`pointer-events-none absolute left-0 top-0 ${className}`}
      style={{ opacity: ready ? 1 : 0, transition: 'opacity 180ms linear' }}>
      <defs>
        {/* The same blue → violet the pill has always used. */}
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#3B82F6" />
          <stop offset="100%" stopColor="#4F46E5" />
        </linearGradient>
      </defs>
      <path ref={path} fill={`url(#${gradientId})`} />
    </svg>
  );
}
